import { useMemo, forwardRef, useImperativeHandle } from 'react';
import { Stack, Typography } from '@mui/material';
import { SXStyleSheet } from '@/utils';
import style from './style';
import { useSubItemsNavigation } from './useSubItemsNavigation';
import SubItemsList from './SubItemsList';
import { NavigationBarItemType } from '@/components/ui/NavigationBar/types';
export interface SideBarTooltipItem {
  path: string;
  icon?: string;
  label?: string;
  items?: SideBarTooltipItem[];
}

export interface SideBarHoverTooltipProps {
  isVisible: boolean;
  labels: string[];
  activeIndex?: number;
  /** Gemessene vertikale Mitte des gehoverten Items, relativ zur Sidebar (px).
   *  Wenn gesetzt, richtet sich der Tooltip daran aus (robust gegen Layout-
   *  Aenderungen) statt ueber eine index-basierte Offset-Formel. */
  top?: number;
  items?: SideBarTooltipItem[];
  hasBackButton?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onSideBarHover?: () => void;
  /**
   * Klickziele vom Consumer statt aus der Route (feedback-fixes-2026-07-22.md
   * Punkt 6). Die Beratungsstrecke nutzt denselben Flyout wie die CRM-Sidebar,
   * navigiert aber nicht: ihre "Unterpunkte" sind Scroll-Sektionen EINER Seite.
   * Ohne diese Prop bleibt das Routing-Verhalten der CRM-Sidebar unveraendert.
   */
  sectionNavigation?: {
    subItemsFor: (index: number) => NavigationBarItemType[];
    /** Aktive Sektion (Scroll-Position), da useMatches hier nie greift. */
    activePath?: string;
    onLabelClick: (index: number) => void;
    onSubItemClick: (index: number, subItemPath: string) => void;
  };
}

export interface SideBarHoverTooltipRef {
  resetState: () => void;
}

const SideBarHoverTooltip = forwardRef<
  SideBarHoverTooltipRef,
  SideBarHoverTooltipProps
>(
  (
    {
      isVisible,
      labels,
      activeIndex,
      top,
      items = [],
      hasBackButton = false,
      onMouseEnter,
      onMouseLeave,
      onSideBarHover,
      sectionNavigation,
    },
    ref,
  ) => {
    useImperativeHandle(
      ref,
      () => ({
        resetState: () => {},
      }),
      [],
    );

    // Der Routing-Hook laeuft unabhaengig weiter (Hook-Regeln); welche
    // Klickziele tatsaechlich gelten, entscheidet sectionNavigation.
    const routeNavigation = useSubItemsNavigation(items, activeIndex);

    const subItems = sectionNavigation && activeIndex !== undefined
      ? sectionNavigation.subItemsFor(activeIndex)
      : routeNavigation.subItems;

    const handleSubItemClick = sectionNavigation && activeIndex !== undefined
      ? (subItemPath: string) => sectionNavigation.onSubItemClick(activeIndex, subItemPath)
      : routeNavigation.handleSubItemClick;

    const handleLabelClick = sectionNavigation && activeIndex !== undefined
      ? () => sectionNavigation.onLabelClick(activeIndex)
      : routeNavigation.handleLabelClick;

    const currentLabel = useMemo(() => {
      if (activeIndex !== undefined && labels[activeIndex]) {
        return labels[activeIndex];
      }
      return '';
    }, [activeIndex, labels]);

    const currentItemIcon = useMemo(() => {
      if (activeIndex !== undefined && items[activeIndex]) {
        return items[activeIndex].icon;
      }
      return '';
    }, [activeIndex, items]);

    const currentItemPath = useMemo(() => {
      if (activeIndex !== undefined && items[activeIndex]) {
        return items[activeIndex].path;
      }
      return '';
    }, [activeIndex, items]);

    if (!isVisible || activeIndex === undefined) return null;

    // Tooltip vertikal am tatsaechlich gehoverten Item ausrichten: `top` ist
    // die gemessene Item-Mitte relativ zur Sidebar (robust gegen Layout-
    // Aenderungen). Wir ziehen die Label-Mitte des Tooltips ab, damit das
    // Label auf Icon-Hoehe sitzt.
    // Fallback (kein gemessener Wert) nutzt die realen Item-Slot-Masse:
    // 44px Item-Hoehe + 4px margin-bottom (mb={1}) = 48px pro Slot.
    const LABEL_CENTER_FROM_TOP = 12 + 36 / 2; // container padding-top + label-height/2
    const FALLBACK_ITEM_SLOT = 48;
    const FALLBACK_INITIAL = hasBackButton ? 120 : 65;
    const topPosition =
      top !== undefined
        ? top - LABEL_CENTER_FROM_TOP
        : FALLBACK_INITIAL + activeIndex * FALLBACK_ITEM_SLOT;

    return (
      <Stack
        sx={[style.container, { top: topPosition }]}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <Stack sx={style.mainItemContainer}>
          <Typography
            variant="wertBetont"
            sx={SXStyleSheet.merge(style.label, style.activeLabel)}
            onClick={() =>
              handleLabelClick(currentItemPath, currentItemIcon ?? '')
            }
          >
            {currentLabel}
          </Typography>

          <SubItemsList
            subItems={subItems}
            onSubItemClick={handleSubItemClick}
            mainItemIcon={currentItemIcon ?? ''}
            activePath={sectionNavigation?.activePath}
          />
        </Stack>
      </Stack>
    );
  },
);

SideBarHoverTooltip.displayName = 'SideBarHoverTooltip';

export default SideBarHoverTooltip;
